import { Entity, MappedEntityModel, NxTable } from '@nx-data/nx-data';
import { throwError } from '@nx-data/utilities';
import { combineLatest, debounceTime, Observable, ReplaySubject, Subject, Subscription, throttleTime } from 'rxjs';
import { EventDispatcher } from '../../../nx-data/src/lib/events/nx-events';
import { DataLoaderEvents } from './data-loader.events';
import { getTableConfigForEntity } from './table.decorator';

class ReadableSingleReplayedStream<T> extends ReplaySubject<T> {
  private lastEmittedValue: T | undefined = undefined;
  private _emitted = false;

  constructor() {
    super(1);
  }

  public override next(value: T) {
    this.lastEmittedValue = value;
    this._emitted = true;
    super.next(value);
  }

  public getLastValue(): T {
    return this._emitted ? throwError('Subject has no emitted value') : (this.lastEmittedValue as T);
  }

  public get emitted(): boolean {
    return this._emitted;
  }
}

class TableStream<T> extends ReplaySubject<T[]> {
  private readonly streams: Set<Observable<T>> = new Set<Observable<T>>();
  private readonly subscription?: Subscription;

  private readonly streamDebounceMs = 20;

  constructor() {
    super(1);
    super.next([]);
  }

  private resubscribe() {
    this.subscription?.unsubscribe();

    if (this.streams.size === 0) {
      super.next([]);
    }

    combineLatest([...this.streams])
      .pipe(debounceTime(this.streamDebounceMs))
      .subscribe((result) => super.next(result));
  }

  public addStream(stream$: ReplaySubject<T>) {
    this.streams.add(stream$);
    this.resubscribe();
  }

  public addStreams(streams: ReplaySubject<T>[]) {
    for (const stream$ of streams) {
      this.streams.add(stream$);
    }

    this.resubscribe();
  }

  public removeStream(stream$: ReplaySubject<T>) {
    this.streams.delete(stream$);
    this.resubscribe();
  }

  public override next(value: T[]) {
    throwError('Next must not be called on this stream type');
  }
}

export class ObservableTable<T extends Entity> extends NxTable<T> {
  private readonly entityStreams: Record<string, ReadableSingleReplayedStream<MappedEntityModel<T>>> = {};

  private readonly _table$: TableStream<MappedEntityModel<T>> = new TableStream<MappedEntityModel<T>>();

  private readonly _entities$ = this._table$.asObservable();

  public readonly loaderEventBus = new EventDispatcher<DataLoaderEvents>();

  private readonly tableConfig = getTableConfigForEntity(this.classType);

  private readonly allLoaderStream$ = new Subject<void>();

  private readonly allLoaderSubscription = this.allLoaderStream$.pipe(throttleTime(this.tableConfig.entityTtlMs)).subscribe(() => {
    this.loaderEventBus.dispatchEvent('loadAll', undefined);
  });

  protected override entityUpdated(entity: T) {
    super.entityUpdated(entity);

    this.entityStreams[entity.id]?.next(this.get(entity.id));
  }

  protected entitiesUpdated(entities: T[]) {
    super.entitiesUpdated(entities);

    for (const entity of entities) {
      this.entityStreams[entity.id]?.next(this.get(entity.id));
    }
  }

  private getOrCreateObservable$(id: string): ReadableSingleReplayedStream<MappedEntityModel<T>> {
    if (id in this.entityStreams) {
      if (!this.entityStreams[id].emitted && this.has(id)) {
        this.entityStreams[id].next(this.get(id));
      } else if (!this.has(id)) {
        this.loaderEventBus.dispatchEvent('loadEntity', id);
      } else if (this.has(id) && (this.get(id).lastUpdateTimestamp as number) + this.tableConfig.entityTtlMs < Date.now()) {
        this.loaderEventBus.dispatchEvent('loadEntity', id);
      }

      return this.entityStreams[id];
    }

    const stream$ = new ReadableSingleReplayedStream<MappedEntityModel<T>>();

    const entity = this.has(id) ? this.get(id) : null;

    if (entity != null) {
      stream$.next(entity);
    }

    this.storeStream(id, stream$);
    return stream$;
  }

  private storeStream(id: string, stream$: ReadableSingleReplayedStream<MappedEntityModel<T>>) {
    this.entityStreams[id] = stream$;
  }

  protected override entityInserted(entity: T) {
    const stream$ = this.getOrCreateObservable$(entity.id);
    this._table$.addStream(stream$);
  }

  protected override entitiesInserted(entities: T[]) {
    const streams = entities.map((it) => ({ stream$: this.getOrCreateObservable$(it.id), id: it.id }));
    this._table$.addStreams(streams.map((it) => it.stream$));
  }

  public override delete(id: string): MappedEntityModel<T> {
    const entity = super.delete(id);
    const stream$ = this.entityStreams[id];
    this._table$.removeStream(stream$);
    delete this.entityStreams[id];
    return entity;
  }

  public get$(id: string): Observable<MappedEntityModel<T>> {
    return this.getOrCreateObservable$(id);
  }

  public get entities$(): Observable<MappedEntityModel<T>[]> {
    return this._entities$;
  }

  public dispose() {
    super.dispose();
    this.loaderEventBus.dispose();
    this.allLoaderSubscription.unsubscribe();
    this.allLoaderStream$.complete();
  }

  public hasLive(id: string) {
    return this.has(id) && (this.get(id).lastUpdateTimestamp as number) + this.tableConfig.entityTtlMs > Date.now();
  }
}
